// features/PhotoEditor/index.js - Main PhotoEditor component
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaAdjust, FaCrop, FaUndo, FaRedo, FaSave, FaDownload, FaSync, FaArrowsAlt } from 'react-icons/fa';
import { MdRotate90DegreesCcw, MdFlip } from 'react-icons/md';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PhotoEditorProvider } from '../../context/PhotoEditorContext';
import PhotoEditorPreview from '../../components/PhotoEditorPreview';
import usePhotoEditor from '../../hooks/usePhotoEditor';
import usePhotoColourAdjuster from '../../hooks/usePhotoColourAdjuster';
import usePhotoTransformer from '../../hooks/usePhotoTransformer';
import apiService from '../../services/api';
import { fileToDataURL } from '../../utils/helpers';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const PhotoEditor = ({ initialImage, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('adjust');
  const [fileURL, setFileURL] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const parentRef = useRef(null);
  
  // Color adjustment state
  const [adjustmentValues, setAdjustmentValues] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    blur: 0,
    invert: false
  });
  
  // Load initial image
  useEffect(() => {
    if (!initialImage) return;
    
    const loadImage = async () => {
      try {
        setIsLoading(true);
        
        // If initialImage is a URL string
        if (typeof initialImage === 'string') {
          setFileURL(initialImage);
        } 
        // If initialImage is a File object
        else if (initialImage instanceof File) {
          const dataURL = await fileToDataURL(initialImage);
          setFileURL(dataURL);
        }
        
        setError(null);
      } catch (error) {
        console.error('Error loading image:', error);
        setError(t('errors.loadingFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    
    loadImage();
  }, [initialImage, t]);
  
  // Render the editor content
  const EditorContent = () => {
    // Use the photo editor hooks
    const { adjustments } = usePhotoColourAdjuster({
      values: adjustmentValues
    });
    
    const transformer = usePhotoTransformer();
    const photoEditor = usePhotoEditor();
    
    // Handle saving the edited image
    const handleSave = async () => {
      try {
        setIsLoading(true);
        
        // Export the edited image as a data URL
        const dataURL = photoEditor.exportToDataURL();
        if (!dataURL) {
          throw new Error(t('errors.exportFailed'));
        }
        
        // Call the onSave callback with the edited image
        if (onSave) {
          await onSave(dataURL);
        }
        
        setError(null);
      } catch (error) {
        console.error('Error saving image:', error);
        setError(error.message || t('errors.savingFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    
    // Handle downloading the edited image
    const handleDownload = async () => {
      try {
        setIsLoading(true);
        
        // Export the edited image as a data URL
        const dataURL = photoEditor.exportToDataURL();
        if (!dataURL) {
          throw new Error(t('errors.exportFailed'));
        }
        
        // Create a download link
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = 'edited-image.jpg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setError(null);
      } catch (error) {
        console.error('Error downloading image:', error);
        setError(error.message || t('errors.exportFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    
    // Handle resetting all adjustments
    const handleReset = () => {
      setAdjustmentValues({
        brightness: 100,
        contrast: 100,
        saturation: 100,
        blur: 0,
        invert: false
      });
    };

    // Handle slider change
    const handleSliderChange = (property, event) => {
      const value = parseInt(event.target.value, 10);
      setAdjustmentValues(prev => ({ ...prev, [property]: value }));
    };
    
    return (
      <div className="flex flex-row h-full">
        {/* Tools panel - 1/3 width */}
        <div className="w-1/3 pr-4 overflow-y-auto">
          <div className="w-full mb-4">
            <div className="inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-gray-500 dark:bg-gray-800 dark:text-gray-400 w-full grid grid-cols-3">
              <button 
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'adjust' ? 'bg-white text-gray-950 shadow-sm dark:bg-gray-950 dark:text-gray-50' : ''}`}
                onClick={() => setActiveTab('adjust')}
              >
                <FaAdjust className="mr-2" />
                <span className="hidden sm:inline">{t('photoEditor.adjust')}</span>
              </button>
              <button 
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'crop' ? 'bg-white text-gray-950 shadow-sm dark:bg-gray-950 dark:text-gray-50' : ''}`}
                onClick={() => setActiveTab('crop')}
              >
                <FaCrop className="mr-2" />
                <span className="hidden sm:inline">{t('photoEditor.crop')}</span>
              </button>
              <button 
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'rotate' ? 'bg-white text-gray-950 shadow-sm dark:bg-gray-950 dark:text-gray-50' : ''}`}
                onClick={() => setActiveTab('rotate')}
              >
                <MdRotate90DegreesCcw className="mr-2" />
                <span className="hidden sm:inline">{t('photoEditor.rotate')}</span>
              </button>
            </div>
          </div>
            
          {activeTab === 'adjust' && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('photoEditor.brightness')}: {adjustmentValues.brightness}%
                </label>
                <input
                  type="range"
                  min={0}
                  max={200}
                  step={1}
                  value={adjustmentValues.brightness}
                  onChange={(e) => handleSliderChange('brightness', e)}
                  className="h-2 w-full appearance-none rounded-full bg-gray-200 accent-primary range-slider"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('photoEditor.contrast')}: {adjustmentValues.contrast}%
                </label>
                <input
                  type="range"
                  min={0}
                  max={200}
                  step={1}
                  value={adjustmentValues.contrast}
                  onChange={(e) => handleSliderChange('contrast', e)}
                  className="h-2 w-full appearance-none rounded-full bg-gray-200 accent-primary range-slider"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('photoEditor.saturation')}: {adjustmentValues.saturation}%
                </label>
                <input
                  type="range"
                  min={0}
                  max={200}
                  step={1}
                  value={adjustmentValues.saturation}
                  onChange={(e) => handleSliderChange('saturation', e)}
                  className="h-2 w-full appearance-none rounded-full bg-gray-200 accent-primary range-slider"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('photoEditor.blur')}: {adjustmentValues.blur}px
                </label>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={0.1}
                  value={adjustmentValues.blur}
                  onChange={(e) => handleSliderChange('blur', e)}
                  className="h-2 w-full appearance-none rounded-full bg-gray-200 accent-primary range-slider"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="invert"
                  checked={adjustmentValues.invert}
                  onChange={(e) => setAdjustmentValues(prev => ({ ...prev, invert: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="invert" className="text-sm font-medium">
                  {t('photoEditor.invert')}
                </label>
              </div>
              
              <Button 
                onClick={handleReset} 
                variant="outline" 
                className="w-full flex items-center justify-center gap-2"
              >
                <FaSync />
                <span>{t('photoEditor.reset')}</span>
              </Button>
            </div>
          )}
            
          {activeTab === 'crop' && (
            <div className="mt-4">
              <div className="grid grid-cols-2 gap-4">
                <Button 
                  onClick={() => transformer.cropImage(1, 1)} 
                  variant="outline"
                >
                  1:1
                </Button>
                <Button 
                  onClick={() => transformer.cropImage(4, 3)} 
                  variant="outline"
                >
                  4:3
                </Button>
                <Button 
                  onClick={() => transformer.cropImage(16, 9)} 
                  variant="outline"
                >
                  16:9
                </Button>
                <Button 
                  onClick={() => transformer.cropImage(3, 4)} 
                  variant="outline"
                >
                  3:4
                </Button>
              </div>
            </div>
          )}
            
          {activeTab === 'rotate' && (
            <div className="mt-4">
              <div className="grid grid-cols-2 gap-4">
                <Button 
                  onClick={() => transformer.rotateImage(90)} 
                  variant="outline" 
                  className="flex items-center justify-center gap-2"
                >
                  <MdRotate90DegreesCcw />
                  <span>{t('photoEditor.rotate90')}</span>
                </Button>
                <Button 
                  onClick={() => transformer.flipImage('horizontal')} 
                  variant="outline" 
                  className="flex items-center justify-center gap-2"
                >
                  <MdFlip className="transform rotate-90" />
                  <span>{t('photoEditor.flipHorizontal')}</span>
                </Button>
                <Button 
                  onClick={() => transformer.rotateImage(-90)} 
                  variant="outline" 
                  className="flex items-center justify-center gap-2"
                >
                  <MdRotate90DegreesCcw className="transform -scale-x-100" />
                  <span>{t('photoEditor.rotate270')}</span>
                </Button>
                <Button 
                  onClick={() => transformer.flipImage('vertical')} 
                  variant="outline" 
                  className="flex items-center justify-center gap-2"
                >
                  <MdFlip />
                  <span>{t('photoEditor.flipVertical')}</span>
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex justify-between mt-6">
            <Button 
              onClick={onCancel} 
              variant="outline" 
              className="flex-1 mr-2"
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              variant="primary" 
              className="flex-1 ml-2 flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
              ) : (
                <FaSave />
              )}
              <span>{t('common.save')}</span>
            </Button>
          </div>
          
          <Button 
            onClick={handleDownload} 
            variant="secondary" 
            className="w-full mt-2 flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            <FaDownload />
            <span>{t('common.download')}</span>
          </Button>
          
          {error && (
            <div className="mt-4 p-2 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-md">
              {error}
            </div>
          )}
        </div>
        
        {/* Image preview - 2/3 width */}
        <div className="w-2/3 overflow-hidden">
          <div 
            ref={parentRef} 
            className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-900 rounded-lg"
          >
            <PhotoEditorPreview show={true} />
          </div>
        </div>
      </div>
    );
  };
  
  // If no image is provided, show a placeholder
  if (!fileURL) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 bg-gray-100 dark:bg-gray-900 rounded-lg">
        <FaArrowsAlt className="text-4xl mb-4 text-gray-400" />
        <p className="text-center text-gray-500">
          {t('photoEditor.noImage')}
        </p>
      </div>
    );
  }
  
  return (
    <motion.div 
      className="h-full"
      initial="hidden"
      animate="visible"
      variants={fadeIn}
    >
      <PhotoEditorProvider
        value={{
          fileURL,
          parentElementRef: parentRef,
          outputMime: 'image/jpeg'
        }}
      >
        <EditorContent />
      </PhotoEditorProvider>
    </motion.div>
  );
};

export default PhotoEditor;
